import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroSlider from "@/components/HeroSlider";
import RentalBookingBanner from "@/components/RentalBookingBanner";
import ServicesMenu from "@/components/ServicesMenu";
import CarCard from "@/components/CarCard";
import HomeAdminOverlay from "@/components/HomeAdminOverlay";
import HomeMaintenancePopup from "@/components/HomeMaintenancePopup";
import HomeSectionControls from "@/components/HomeSectionControls";
import CustomHomeSections from "@/components/CustomHomeSections";
import CategoriesCarousel from "@/components/CategoriesCarousel";
import { InlineEditableItem, AddInlineItem } from "@/components/InlineEditable";
import { api, apiGetCached } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Star, ShieldCheck, Clock, Wallet, Banknote, CreditCard } from "lucide-react";

const CATEGORIES_DEFAULT = [
  { key: "sportiva", label: "SPORTIVE", img: "https://images.pexels.com/photos/34404450/pexels-photo-34404450.jpeg" },
  { key: "supercar", label: "SUPERCAR", img: "https://images.pexels.com/photos/337909/pexels-photo-337909.jpeg?w=1200&auto=compress" },
  { key: "suv", label: "SUV", img: `${process.env.REACT_APP_BACKEND_URL}/api/static/images/audi_q5_suv.png` },
  { key: "berlina", label: "BERLINE", img: "https://images.unsplash.com/photo-1645596309663-b7b3c795ad85?w=1200" },
  { key: "citycar", label: "CITY CAR", img: "https://customer-assets.emergentagent.com/job_fleet-marketplace-6/artifacts/34bkpecu_Screenshot%202026-05-11%20alle%2012.31.09.png" },
  { key: "monovolume", label: "MONOVOLUME", img: `${process.env.REACT_APP_BACKEND_URL}/api/static/images/monovolume.png` },
];

const TESTIMONIALS_DEFAULT = [
  { name: "Elisabetta Canofari", city: "Roma", text: "Personale qualificato, gentile e simpatico. Ho noleggiato per un mese una Mini Countryman fantastica." },
  { name: "Marco Garulli", city: "Roma", text: "Mio cugino noleggia da più di 2 mesi, si trova benissimo. Servizio impeccabile." },
  { name: "Alessandro Rinaldi", city: "Roma", text: "Grande serietà e professionalità. Uno dei pochi rivenditori che noleggia anche super sportive." },
  { name: "Piero Saccucci", city: "Roma", text: "Da circa un anno cliente, molto disponibili, gestione del noleggio adattata alle mie esigenze." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES_DEFAULT);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS_DEFAULT);
  const [depositInfo, setDepositInfo] = useState({ from: 500, default: true });

  useEffect(() => {
    apiGetCached("/cars", { ttlMs: 30_000, params: { show_on_home: true, light: true } }).then((data) => setFeatured(data.slice(0, 8)));
    api.get("/site-config").then((r) => {
      if (r.data.categories?.length) setCategories(r.data.categories);
      if (r.data.testimonials?.length) setTestimonials(r.data.testimonials);
    }).catch(() => {});
    api.get("/public/deposit-info").then((r) => setDepositInfo(r.data)).catch(() => {});
  }, []);

  // Persist edits to the site-config; also broadcasts a `hero-updated` event for consistency
  const saveCfg = async (patch) => {
    try {
      await api.put("/admin/site-config", patch);
      toast.success("Salvato");
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const updateCategory = (i, field, value) => {
    const next = categories.map((c, idx) => (idx === i ? { ...c, [field]: value } : c));
    setCategories(next);
    saveCfg({ categories: next, testimonials });
  };
  const deleteCategory = (i) => {
    const next = categories.filter((_, idx) => idx !== i);
    setCategories(next);
    saveCfg({ categories: next, testimonials });
  };
  const addCategory = () => {
    const key = `nuova-${Date.now().toString(36)}`;
    const next = [...categories, { key, label: "NUOVA CATEGORIA", img: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200" }];
    setCategories(next);
    saveCfg({ categories: next, testimonials });
  };

  const updateTestimonial = (i, field, value) => {
    const next = testimonials.map((t, idx) => (idx === i ? { ...t, [field]: value } : t));
    setTestimonials(next);
    saveCfg({ testimonials: next, categories });
  };
  const deleteTestimonial = (i) => {
    const next = testimonials.filter((_, idx) => idx !== i);
    setTestimonials(next);
    saveCfg({ testimonials: next, categories });
  };
  const addTestimonial = () => {
    const next = [...testimonials, { name: "Nuovo Cliente", city: "Roma", text: "Ottima esperienza con +Utile Rent." }];
    setTestimonials(next);
    saveCfg({ testimonials: next, categories });
  };

  return (
    <div>
      <HomeMaintenancePopup />
      <HomeAdminOverlay />
      <HomeSectionControls />
      <HeroSlider />

      <CustomHomeSections />

      <div data-home-section="booking" data-home-label="Prenota la tua auto" className="relative">
        <RentalBookingBanner />
      </div>

      {/* Deposito cauzionale — accepted payment methods */}
      <section data-testid="deposit-info" data-home-section="deposit" data-home-label="Deposito cauzionale" className="relative bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
            <div className="shrink-0">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#22ff66] font-bold mb-2">Deposito cauzionale</div>
              <h3 className="heading-uc text-2xl md:text-3xl text-white leading-tight">
                Lo accettiamo <span className="text-[#22ff66]">in 3 modi</span>
              </h3>
              <div data-testid="deposit-from" className="mt-3 inline-flex items-baseline gap-2 border-l-2 border-[#22ff66] pl-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">da</span>
                <span className="font-display font-black text-3xl md:text-4xl text-white">
                  €{Number(depositInfo.from || 500).toLocaleString("it-IT")}
                </span>
              </div>
              <div className="text-[10px] text-white/40 mt-1 max-w-[260px]">
                Importo variabile in base alla categoria della vettura
              </div>
            </div>
            <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
              {[
                { Icon: Banknote, t: "Contanti", d: "Pagamento in agenzia al ritiro" },
                { Icon: CreditCard, t: "Carta di credito", d: "Preautorizzazione temporanea" },
                { Icon: Wallet, t: "Carta di debito", d: "Addebito immediato" },
              ].map((d, i) => (
                <div
                  key={i}
                  data-testid={`deposit-method-${i}`}
                  className="group border border-white/10 hover:border-[#22ff66]/60 bg-[#0e0e0e] p-5 flex items-start gap-4 transition-all hover:bg-[#111] hover:-translate-y-0.5"
                >
                  <div className="w-11 h-11 shrink-0 bg-[#22ff66] text-black flex items-center justify-center">
                    <d.Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-white text-sm md:text-base uppercase tracking-wider leading-tight">{d.t}</div>
                    <div className="text-xs text-white/50 mt-0.5">{d.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div data-home-section="services" data-home-label="Menu dei servizi" className="relative">
        <ServicesMenu />
      </div>

      {/* USP Strip */}
      <section data-home-section="usp" data-home-label="Vantaggi (No carta di credito...)" className="relative bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {[
            { Icon: Wallet, t: "NO CARTA DI CREDITO" },
            { Icon: ShieldCheck, t: "NO SCORING" },
            { Icon: Star, t: "NO BANCHE" },
            { Icon: Clock, t: "CONSEGNA RAPIDA" },
          ].map((u, i) => (
            <div key={i} className="flex items-center gap-4 py-8 px-4 lg:px-8 justify-center">
              <u.Icon className="w-6 h-6 text-[#22ff66]" />
              <span className="heading-uc text-xs sm:text-sm">{u.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section data-testid="categories-section" data-home-section="categories" data-home-label="Categorie" className="relative py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-[#22ff66] text-xs uppercase tracking-[0.3em] font-semibold mb-3">Categorie</div>
              <h2 className="heading-uc text-3xl md:text-5xl">Trova la tua auto</h2>
            </div>
            <Link to="/catalogo" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest font-semibold hover:text-[#22ff66]">
              Tutto il catalogo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="relative">
            <CategoriesCarousel
              categories={categories}
              updateCategory={updateCategory}
              deleteCategory={deleteCategory}
              addCategory={addCategory}
            />
          </div>
        </div>
      </section>

      {/* Lungo Termine CTA — links to /noleggio/lungo-termine */}
      <section data-testid="longterm-cta-home" data-home-section="longterm" data-home-label="Lungo termine" className="relative py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Link
            to="/noleggio/lungo-termine"
            data-testid="longterm-cta-link"
            className="group relative overflow-hidden bg-[#0a0a0a] border border-black/10 hover:border-[#22ff66] transition-all grid grid-cols-1 md:grid-cols-[1.1fr_1fr] min-h-[280px] md:min-h-[320px]"
          >
            <div className="p-8 md:p-12 flex flex-col justify-center text-white z-10">
              <div className="text-[#22ff66] text-[11px] uppercase tracking-[0.4em] font-bold mb-3">
                Noleggio Lungo Termine
              </div>
              <h3 className="heading-uc text-3xl md:text-5xl leading-[1.05] text-white">
                LUNGO <span className="text-[#22ff66]">TERMINE</span>
              </h3>
              <p className="text-white/70 text-sm md:text-base mt-4 max-w-md">
                Da 36 a 60 mesi — zero pensieri su tagliando, assicurazione e bollo. Soluzioni per privati e aziende, con o senza anticipo.
              </p>
              <div className="flex items-baseline gap-3 mt-5">
                <span className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">A partire da</span>
                <span className="font-display font-black text-4xl md:text-5xl text-[#22ff66]">€271</span>
                <span className="text-xs text-white/50">/mese</span>
              </div>
              <div className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-black text-black bg-[#22ff66] h-11 px-5 w-fit group-hover:gap-3 transition-all rounded-full">
                Scopri le offerte <ArrowRight className="w-4 h-4" />
              </div>
            </div>
            <div className="relative overflow-hidden bg-[#111]">
              <img
                src="https://images.unsplash.com/photo-1549924231-f129b911e442?w=1400&q=80"
                alt="Noleggio Lungo Termine"
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0a0a0a]/40 to-[#0a0a0a]" />
            </div>
          </Link>
        </div>
      </section>

      {/* Featured cars — only cars flagged as show_on_home in admin */}
      <section data-testid="featured-section" data-home-section="featured" data-home-label="Vetture in evidenza" className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-[#22ff66] text-xs uppercase tracking-[0.3em] font-semibold mb-3">In Evidenza</div>
              <h2 className="heading-uc text-3xl md:text-5xl">Vetture in evidenza</h2>
            </div>
            <Link to="/catalogo" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest font-semibold hover:text-[#22ff66]">
              Vedi tutte <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {featured.length === 0 ? (
            <div className="border border-white/10 p-16 text-center text-white/40 text-sm">
              Nessuna vettura in evidenza. Attiva il toggle "Mostra in home" su un annuncio dall'area admin.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          )}
        </div>
      </section>

      {/* Subscriptions teaser */}
      <section data-testid="subscriptions-teaser" data-home-section="subscriptions" data-home-label="Abbonamenti" className="relative py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-[#22ff66] text-xs uppercase tracking-[0.3em] font-semibold mb-3">Abbonamenti annuali</div>
            <h2 className="heading-uc text-3xl md:text-5xl mb-4">Una formula. Tre eleganze.</h2>
            <p className="text-white/60 max-w-2xl mx-auto">Scegli il tuo piano: 12 mesi, canone una tantum, l'auto della tua categoria sempre pronta.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { tier: "01", plan: "BASIC", price: 99, label: "City Car", img: "https://customer-assets.emergentagent.com/job_fleet-marketplace-6/artifacts/34bkpecu_Screenshot%202026-05-11%20alle%2012.31.09.png" },
              { tier: "02", plan: "MEDIUM", price: 199, label: "Berline", img: "https://images.unsplash.com/photo-1645596309663-b7b3c795ad85?w=900" },
              { tier: "03", plan: "PREMIUM", price: 299, label: "Crossover / SUV", img: "https://static.prod-images.emergentagent.com/jobs/9e7040e3-0412-4f30-bffa-3465ab599eb7/images/f3c6e43430aebea05283849182805cf5a75af68d76312572ef253c4ffb699167.png" },
            ].map((p) => (
              <Link
                key={p.plan}
                to="/abbonamenti"
                data-testid={`sub-teaser-${p.plan.toLowerCase()}`}
                className="group bg-[#0e0e0e] border border-white/10 hover:border-[#22ff66] transition relative overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden bg-black">
                  <img src={p.img} alt={p.plan} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-8 relative">
                  <div className="absolute top-2 right-6 tier-num">{p.tier}</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{p.label}</div>
                  <h3 className="heading-uc text-3xl mb-4">{p.plan}</h3>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="font-display text-5xl font-black text-[#22ff66]">€{p.price}</span>
                    <span className="text-sm text-white/50">/anno</span>
                  </div>
                  <div className="text-sm uppercase tracking-widest text-white/80 group-hover:text-[#22ff66] transition flex items-center gap-2">
                    Scegli piano <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" data-home-section="testimonials" data-home-label="Testimonianze" className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-[#22ff66] text-xs uppercase tracking-[0.3em] font-semibold mb-3">Testimonianze</div>
            <h2 className="heading-uc text-3xl md:text-5xl">I nostri clienti</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <InlineEditableItem
                key={i}
                title={t.name}
                label="testimonianza"
                onEditTitle={(v) => updateTestimonial(i, "text", v)}
                onDelete={() => deleteTestimonial(i)}
              >
                <div className="bg-[#050505] border border-white/10 p-6 h-full">
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#22ff66] text-[#22ff66]" />)}
                  </div>
                  <p className="text-sm text-white/70 leading-relaxed mb-6">"{t.text}"</p>
                  <div className="text-xs uppercase tracking-widest text-white font-semibold">{t.name}</div>
                  <div className="text-xs text-white/40 mt-1">{t.city}, Italia</div>
                </div>
              </InlineEditableItem>
            ))}
            <AddInlineItem testid="add-testimonial" label="Aggiungi testimonianza" onClick={addTestimonial} aspect="min-h-[220px]" />
          </div>
        </div>
      </section>
    </div>
  );
}
